// Shared theme data — no 'use client' directive so this can be imported
// by both Server Components (e.g. /p/[slug]/page.tsx) and Client Components.

export type ThemeName =
  | 'default' |'anomica' |'anomica-light' |'pearson' |'restly' |'invoicera' |'royalblue' |'oceanblue' |'ember' |'sage' |'midnight' |'planix';

export interface ThemeConfig {
  name: ThemeName;
  label: string;
  description: string;
  preview: {
    bg: string;
    sidebar: string;
    accent: string;
    card: string;
  };
  vars: Record<string, string>;
}

export const THEMES: ThemeConfig[] = [
  {
    name: 'default',
    label: 'Default (Grind)',
    description: 'Earthy greens, birch warmth, amber accents — the original Cards247 look.',
    preview: { bg: '#f4f0e8', sidebar: '#1b3a2d', accent: '#d4a03c', card: '#ffffff' },
    vars: {
      '--color-bg': '#f4f0e8',
      '--color-sidebar': '#1b3a2d',
      '--color-sidebar-text': 'rgba(244,240,232,0.85)',
      '--color-sidebar-active-bg': 'rgba(212,160,60,0.18)',
      '--color-sidebar-active-text': '#d4a03c',
      '--color-sidebar-hover-bg': 'rgba(255,255,255,0.06)',
      '--color-sidebar-border': 'rgba(255,255,255,0.08)',
      '--color-sidebar-logo-text': '#f4f0e8',
      '--color-primary': '#1f525d',
      '--color-primary-light': 'rgba(31,82,93,0.1)',
      '--color-accent': '#d4a03c',
      '--color-accent-dark': '#b8872a',
      '--color-accent-light': 'rgba(212,160,60,0.1)',
      '--color-card-bg': '#ffffff',
      '--color-card-border': 'rgba(27,58,45,0.1)',
      '--color-card-shadow': '0 2px 12px rgba(27,58,45,0.06)',
      '--color-text-primary': '#1b3a2d',
      '--color-text-secondary': 'rgba(27,58,45,0.6)',
      '--color-text-muted': 'rgba(27,58,45,0.4)',
      '--color-input-bg': '#ffffff',
      '--color-input-border': 'rgba(27,58,45,0.2)',
      '--color-input-focus': 'rgba(31,82,93,0.15)',
      '--color-divider': 'rgba(27,58,45,0.1)',
      '--color-section-label': '#1f525d',
      '--color-btn-primary-bg': '#d4a03c',
      '--color-btn-primary-text': '#1b3a2d',
      '--color-btn-primary-hover': '#b8872a',
      '--color-btn-outline-border': 'rgba(27,58,45,0.2)',
      '--color-btn-outline-text': '#1b3a2d',
      '--color-tab-active-bg': '#1b3a2d',
      '--color-tab-active-text': '#f4f0e8',
      '--color-tab-inactive-text': 'rgba(27,58,45,0.55)',
      '--color-badge-bg': 'rgba(31,82,93,0.1)',
      '--color-badge-text': '#1f525d',
      '--font-heading': "'DM Serif Display', serif",
      '--font-body': "'IBM Plex Sans', sans-serif",
      '--radius-card': '1rem',
      '--radius-btn': '0.75rem',
      '--radius-input': '0.875rem',
    },
  },
  {
    name: 'anomica',
    label: 'Anomica',
    description: 'Corporate IT-tech aesthetic — deep navy sidebar, Poppins typography, blue-orange accents.',
    preview: { bg: '#f7f9fe', sidebar: '#263045', accent: '#2d4a8a', card: '#ffffff' },
    vars: {
      '--color-bg': '#f7f9fe',
      '--color-sidebar': '#263045',
      '--color-sidebar-text': 'rgba(255,255,255,0.85)',
      '--color-sidebar-active-bg': 'rgba(45,74,138,0.35)',
      '--color-sidebar-active-text': '#ffffff',
      '--color-sidebar-hover-bg': 'rgba(255,255,255,0.08)',
      '--color-sidebar-border': 'rgba(255,255,255,0.1)',
      '--color-sidebar-logo-text': '#ffffff',
      '--color-primary': '#2d4a8a',
      '--color-primary-light': 'rgba(45,74,138,0.1)',
      '--color-accent': '#fe5603',
      '--color-accent-dark': '#e04a00',
      '--color-accent-light': 'rgba(254,86,3,0.1)',
      '--color-card-bg': '#ffffff',
      '--color-card-border': 'rgba(45,74,138,0.12)',
      '--color-card-shadow': '0 4px 20px rgba(45,74,138,0.08)',
      '--color-text-primary': '#1e2637',
      '--color-text-secondary': 'rgba(30,38,55,0.65)',
      '--color-text-muted': 'rgba(30,38,55,0.4)',
      '--color-input-bg': '#ffffff',
      '--color-input-border': 'rgba(45,74,138,0.2)',
      '--color-input-focus': 'rgba(45,74,138,0.18)',
      '--color-divider': 'rgba(45,74,138,0.1)',
      '--color-section-label': '#2d4a8a',
      '--color-btn-primary-bg': '#2d4a8a',
      '--color-btn-primary-text': '#ffffff',
      '--color-btn-primary-hover': '#1e3570',
      '--color-btn-outline-border': 'rgba(45,74,138,0.3)',
      '--color-btn-outline-text': '#2d4a8a',
      '--color-tab-active-bg': '#2d4a8a',
      '--color-tab-active-text': '#ffffff',
      '--color-tab-inactive-text': 'rgba(30,38,55,0.5)',
      '--color-badge-bg': 'rgba(45,74,138,0.1)',
      '--color-badge-text': '#2d4a8a',
      '--font-heading': "'Poppins', 'Plus Jakarta Sans', sans-serif",
      '--font-body': "'Poppins', 'Plus Jakarta Sans', sans-serif",
      '--radius-card': '0.5rem',
      '--radius-btn': '0.5rem',
      '--radius-input': '0.5rem',
    },
  },
  {
    name: 'anomica-light',
    label: 'Anomica Light',
    description: 'Anomica colors on a crisp white canvas — bright, airy, and professional.',
    preview: { bg: '#ffffff', sidebar: '#2d4a8a', accent: '#fe5603', card: '#f7f9fe' },
    vars: {
      '--color-bg': '#ffffff',
      '--color-sidebar': '#2d4a8a',
      '--color-sidebar-text': 'rgba(255,255,255,0.88)',
      '--color-sidebar-active-bg': 'rgba(255,255,255,0.2)',
      '--color-sidebar-active-text': '#ffffff',
      '--color-sidebar-hover-bg': 'rgba(255,255,255,0.1)',
      '--color-sidebar-border': 'rgba(255,255,255,0.15)',
      '--color-sidebar-logo-text': '#ffffff',
      '--color-primary': '#2d4a8a',
      '--color-primary-light': 'rgba(45,74,138,0.08)',
      '--color-accent': '#fe5603',
      '--color-accent-dark': '#e04a00',
      '--color-accent-light': 'rgba(254,86,3,0.08)',
      '--color-card-bg': '#f7f9fe',
      '--color-card-border': 'rgba(45,74,138,0.1)',
      '--color-card-shadow': '0 2px 16px rgba(45,74,138,0.07)',
      '--color-text-primary': '#1e2637',
      '--color-text-secondary': 'rgba(30,38,55,0.6)',
      '--color-text-muted': 'rgba(30,38,55,0.38)',
      '--color-input-bg': '#ffffff',
      '--color-input-border': 'rgba(45,74,138,0.18)',
      '--color-input-focus': 'rgba(45,74,138,0.15)',
      '--color-divider': 'rgba(45,74,138,0.08)',
      '--color-section-label': '#2d4a8a',
      '--color-btn-primary-bg': '#2d4a8a',
      '--color-btn-primary-text': '#ffffff',
      '--color-btn-primary-hover': '#1e3570',
      '--color-btn-outline-border': 'rgba(45,74,138,0.28)',
      '--color-btn-outline-text': '#2d4a8a',
      '--color-tab-active-bg': '#2d4a8a',
      '--color-tab-active-text': '#ffffff',
      '--color-tab-inactive-text': 'rgba(30,38,55,0.48)',
      '--color-badge-bg': 'rgba(45,74,138,0.08)',
      '--color-badge-text': '#2d4a8a',
      '--font-heading': "'Poppins', 'Plus Jakarta Sans', sans-serif",
      '--font-body': "'Poppins', 'Plus Jakarta Sans', sans-serif",
      '--radius-card': '0.5rem',
      '--radius-btn': '0.5rem',
      '--radius-input': '0.5rem',
    },
  },
  {
    name: 'pearson',
    label: 'Pearson',
    description: 'Sophisticated law-firm elegance — deep forest green, crisp white, serif refinement.',
    preview: { bg: '#f5f7f5', sidebar: '#1a3a2a', accent: '#3d8b5e', card: '#ffffff' },
    vars: {
      '--color-bg': '#f5f7f5',
      '--color-sidebar': '#1a3a2a',
      '--color-sidebar-text': 'rgba(235,245,238,0.85)',
      '--color-sidebar-active-bg': 'rgba(61,139,94,0.18)',
      '--color-sidebar-active-text': '#5cb87a',
      '--color-sidebar-hover-bg': 'rgba(255,255,255,0.06)',
      '--color-sidebar-border': 'rgba(255,255,255,0.08)',
      '--color-sidebar-logo-text': '#ebf5ee',
      '--color-primary': '#2e7d52',
      '--color-primary-light': 'rgba(46,125,82,0.1)',
      '--color-accent': '#3d8b5e',
      '--color-accent-dark': '#2e7d52',
      '--color-accent-light': 'rgba(61,139,94,0.1)',
      '--color-card-bg': '#ffffff',
      '--color-card-border': 'rgba(26,58,42,0.1)',
      '--color-card-shadow': '0 2px 16px rgba(26,58,42,0.07)',
      '--color-text-primary': '#1a3a2a',
      '--color-text-secondary': 'rgba(26,58,42,0.6)',
      '--color-text-muted': 'rgba(26,58,42,0.38)',
      '--color-input-bg': '#ffffff',
      '--color-input-border': 'rgba(26,58,42,0.18)',
      '--color-input-focus': 'rgba(61,139,94,0.2)',
      '--color-divider': 'rgba(26,58,42,0.08)',
      '--color-section-label': '#2e7d52',
      '--color-btn-primary-bg': '#2e7d52',
      '--color-btn-primary-text': '#ffffff',
      '--color-btn-primary-hover': '#245f3f',
      '--color-btn-outline-border': 'rgba(26,58,42,0.25)',
      '--color-btn-outline-text': '#1a3a2a',
      '--color-tab-active-bg': '#1a3a2a',
      '--color-tab-active-text': '#ebf5ee',
      '--color-tab-inactive-text': 'rgba(26,58,42,0.5)',
      '--color-badge-bg': 'rgba(61,139,94,0.12)',
      '--color-badge-text': '#2e7d52',
      '--font-heading': "'Playfair Display', 'DM Serif Display', serif",
      '--font-body': "'Lato', 'IBM Plex Sans', sans-serif",
      '--radius-card': '0.5rem',
      '--radius-btn': '0.25rem',
      '--radius-input': '0.375rem',
    },
  },
  {
    name: 'restly',
    label: 'Restly',
    description: 'Modern SaaS clarity — clean white, vibrant violet, smooth rounded forms.',
    preview: { bg: '#f5f4ff', sidebar: '#6c5ce7', accent: '#6c5ce7', card: '#ffffff' },
    vars: {
      '--color-bg': '#f5f4ff',
      '--color-sidebar': '#6c5ce7',
      '--color-sidebar-text': 'rgba(255,255,255,0.85)',
      '--color-sidebar-active-bg': 'rgba(255,255,255,0.18)',
      '--color-sidebar-active-text': '#ffffff',
      '--color-sidebar-hover-bg': 'rgba(255,255,255,0.1)',
      '--color-sidebar-border': 'rgba(255,255,255,0.12)',
      '--color-sidebar-logo-text': '#ffffff',
      '--color-primary': '#6c5ce7',
      '--color-primary-light': 'rgba(108,92,231,0.1)',
      '--color-accent': '#fd79a8',
      '--color-accent-dark': '#e84393',
      '--color-accent-light': 'rgba(253,121,168,0.1)',
      '--color-card-bg': '#ffffff',
      '--color-card-border': 'rgba(108,92,231,0.1)',
      '--color-card-shadow': '0 4px 20px rgba(108,92,231,0.08)',
      '--color-text-primary': '#2d3436',
      '--color-text-secondary': 'rgba(45,52,54,0.6)',
      '--color-text-muted': 'rgba(45,52,54,0.4)',
      '--color-input-bg': '#ffffff',
      '--color-input-border': 'rgba(108,92,231,0.2)',
      '--color-input-focus': 'rgba(108,92,231,0.2)',
      '--color-divider': 'rgba(108,92,231,0.1)',
      '--color-section-label': '#6c5ce7',
      '--color-btn-primary-bg': '#6c5ce7',
      '--color-btn-primary-text': '#ffffff',
      '--color-btn-primary-hover': '#5a4bd1',
      '--color-btn-outline-border': 'rgba(108,92,231,0.3)',
      '--color-btn-outline-text': '#6c5ce7',
      '--color-tab-active-bg': '#6c5ce7',
      '--color-tab-active-text': '#ffffff',
      '--color-tab-inactive-text': 'rgba(45,52,54,0.5)',
      '--color-badge-bg': 'rgba(108,92,231,0.1)',
      '--color-badge-text': '#6c5ce7',
      '--font-heading': "'Nunito', 'Plus Jakarta Sans', sans-serif",
      '--font-body': "'Nunito', 'Plus Jakarta Sans', sans-serif",
      '--radius-card': '1.25rem',
      '--radius-btn': '2rem',
      '--radius-input': '0.75rem',
    },
  },
  {
    name: 'invoicera',
    label: 'Invoicera',
    description: 'Energetic SaaS billing — warm white, vibrant orange, clean professional grid.',
    preview: { bg: '#fdf8f4', sidebar: '#1a1a1a', accent: '#f97316', card: '#ffffff' },
    vars: {
      '--color-bg': '#fdf8f4',
      '--color-sidebar': '#1c1c1c',
      '--color-sidebar-text': 'rgba(240,235,228,0.85)',
      '--color-sidebar-active-bg': 'rgba(249,115,22,0.18)',
      '--color-sidebar-active-text': '#fb923c',
      '--color-sidebar-hover-bg': 'rgba(255,255,255,0.06)',
      '--color-sidebar-border': 'rgba(255,255,255,0.07)',
      '--color-sidebar-logo-text': '#f0ebe4',
      '--color-primary': '#ea6c00',
      '--color-primary-light': 'rgba(234,108,0,0.08)',
      '--color-accent': '#f97316',
      '--color-accent-dark': '#ea6c00',
      '--color-accent-light': 'rgba(249,115,22,0.1)',
      '--color-card-bg': '#ffffff',
      '--color-card-border': 'rgba(249,115,22,0.1)',
      '--color-card-shadow': '0 1px 8px rgba(28,28,28,0.08)',
      '--color-text-primary': '#1c1c1c',
      '--color-text-secondary': 'rgba(28,28,28,0.6)',
      '--color-text-muted': 'rgba(28,28,28,0.4)',
      '--color-input-bg': '#ffffff',
      '--color-input-border': 'rgba(249,115,22,0.22)',
      '--color-input-focus': 'rgba(249,115,22,0.18)',
      '--color-divider': 'rgba(249,115,22,0.1)',
      '--color-section-label': '#ea6c00',
      '--color-btn-primary-bg': '#f97316',
      '--color-btn-primary-text': '#ffffff',
      '--color-btn-primary-hover': '#ea6c00',
      '--color-btn-outline-border': 'rgba(249,115,22,0.3)',
      '--color-btn-outline-text': '#ea6c00',
      '--color-tab-active-bg': '#f97316',
      '--color-tab-active-text': '#ffffff',
      '--color-tab-inactive-text': 'rgba(28,28,28,0.5)',
      '--color-badge-bg': 'rgba(249,115,22,0.1)',
      '--color-badge-text': '#ea6c00',
      '--font-heading': "'Inter', 'Plus Jakarta Sans', sans-serif",
      '--font-body': "'Inter', 'Plus Jakarta Sans', sans-serif",
      '--radius-card': '0.75rem',
      '--radius-btn': '0.5rem',
      '--radius-input': '0.5rem',
    },
  },
  {
    name: 'royalblue',
    label: 'Royal Blue',
    description: 'Confident and vibrant — bright royal blue, crisp white, bold modern energy.',
    preview: { bg: '#f0f4ff', sidebar: '#1956db', accent: '#1956db', card: '#ffffff' },
    vars: {
      '--color-bg': '#f0f4ff',
      '--color-sidebar': '#1956db',
      '--color-sidebar-text': 'rgba(255,255,255,0.88)',
      '--color-sidebar-active-bg': 'rgba(255,255,255,0.18)',
      '--color-sidebar-active-text': '#ffffff',
      '--color-sidebar-hover-bg': 'rgba(255,255,255,0.1)',
      '--color-sidebar-border': 'rgba(255,255,255,0.14)',
      '--color-sidebar-logo-text': '#ffffff',
      '--color-primary': '#1956db',
      '--color-primary-light': 'rgba(25,86,219,0.1)',
      '--color-accent': '#4f8ef7',
      '--color-accent-dark': '#1244b8',
      '--color-accent-light': 'rgba(79,142,247,0.12)',
      '--color-card-bg': '#ffffff',
      '--color-card-border': 'rgba(25,86,219,0.1)',
      '--color-card-shadow': '0 4px 20px rgba(25,86,219,0.08)',
      '--color-text-primary': '#0f1f4a',
      '--color-text-secondary': 'rgba(15,31,74,0.6)',
      '--color-text-muted': 'rgba(15,31,74,0.4)',
      '--color-input-bg': '#ffffff',
      '--color-input-border': 'rgba(25,86,219,0.2)',
      '--color-input-focus': 'rgba(25,86,219,0.2)',
      '--color-divider': 'rgba(25,86,219,0.1)',
      '--color-section-label': '#1956db',
      '--color-btn-primary-bg': '#1956db',
      '--color-btn-primary-text': '#ffffff',
      '--color-btn-primary-hover': '#1244b8',
      '--color-btn-outline-border': 'rgba(25,86,219,0.3)',
      '--color-btn-outline-text': '#1956db',
      '--color-tab-active-bg': '#1956db',
      '--color-tab-active-text': '#ffffff',
      '--color-tab-inactive-text': 'rgba(15,31,74,0.5)',
      '--color-badge-bg': 'rgba(25,86,219,0.1)',
      '--color-badge-text': '#1956db',
      '--font-heading': "'Inter', 'Plus Jakarta Sans', sans-serif",
      '--font-body': "'Inter', 'Plus Jakarta Sans', sans-serif",
      '--radius-card': '1rem',
      '--radius-btn': '0.75rem',
      '--radius-input': '0.75rem',
    },
  },
  {
    name: 'oceanblue',
    label: 'Ocean Blue',
    description: 'Deep and trustworthy — rich ocean navy, clean white, professional depth.',
    preview: { bg: '#eef4fa', sidebar: '#025288', accent: '#025288', card: '#ffffff' },
    vars: {
      '--color-bg': '#eef4fa',
      '--color-sidebar': '#025288',
      '--color-sidebar-text': 'rgba(255,255,255,0.88)',
      '--color-sidebar-active-bg': 'rgba(255,255,255,0.16)',
      '--color-sidebar-active-text': '#ffffff',
      '--color-sidebar-hover-bg': 'rgba(255,255,255,0.08)',
      '--color-sidebar-border': 'rgba(255,255,255,0.12)',
      '--color-sidebar-logo-text': '#ffffff',
      '--color-primary': '#025288',
      '--color-primary-light': 'rgba(2,82,136,0.1)',
      '--color-accent': '#0284c7',
      '--color-accent-dark': '#01406b',
      '--color-accent-light': 'rgba(2,132,199,0.12)',
      '--color-card-bg': '#ffffff',
      '--color-card-border': 'rgba(2,82,136,0.1)',
      '--color-card-shadow': '0 4px 20px rgba(2,82,136,0.08)',
      '--color-text-primary': '#012d4a',
      '--color-text-secondary': 'rgba(1,45,74,0.6)',
      '--color-text-muted': 'rgba(1,45,74,0.4)',
      '--color-input-bg': '#ffffff',
      '--color-input-border': 'rgba(2,82,136,0.2)',
      '--color-input-focus': 'rgba(2,82,136,0.2)',
      '--color-divider': 'rgba(2,82,136,0.1)',
      '--color-section-label': '#025288',
      '--color-btn-primary-bg': '#025288',
      '--color-btn-primary-text': '#ffffff',
      '--color-btn-primary-hover': '#01406b',
      '--color-btn-outline-border': 'rgba(2,82,136,0.3)',
      '--color-btn-outline-text': '#025288',
      '--color-tab-active-bg': '#025288',
      '--color-tab-active-text': '#ffffff',
      '--color-tab-inactive-text': 'rgba(1,45,74,0.5)',
      '--color-badge-bg': 'rgba(2,82,136,0.1)',
      '--color-badge-text': '#025288',
      '--font-heading': "'Inter', 'Plus Jakarta Sans', sans-serif",
      '--font-body': "'Inter', 'Plus Jakarta Sans', sans-serif",
      '--radius-card': '1rem',
      '--radius-btn': '0.75rem',
      '--radius-input': '0.75rem',
    },
  },
  {
    name: 'ember',
    label: 'Ember',
    description: 'Luxury executive warmth — ivory canvas, crimson energy, gold highlights.',
    preview: { bg: '#fdf6f0', sidebar: '#3b1a1a', accent: '#dc2626', card: '#ffffff' },
    vars: {
      '--color-bg': '#fdf6f0',
      '--color-sidebar': '#3b1a1a',
      '--color-sidebar-text': 'rgba(255,235,220,0.85)',
      '--color-sidebar-active-bg': 'rgba(220,38,38,0.28)',
      '--color-sidebar-active-text': '#fca5a5',
      '--color-sidebar-hover-bg': 'rgba(255,255,255,0.07)',
      '--color-sidebar-border': 'rgba(255,255,255,0.08)',
      '--color-sidebar-logo-text': '#fdf6f0',
      '--color-primary': '#dc2626',
      '--color-primary-light': 'rgba(220,38,38,0.1)',
      '--color-accent': '#d97706',
      '--color-accent-dark': '#b45309',
      '--color-accent-light': 'rgba(217,119,6,0.1)',
      '--color-card-bg': '#ffffff',
      '--color-card-border': 'rgba(220,38,38,0.12)',
      '--color-card-shadow': '0 2px 16px rgba(59,26,26,0.08)',
      '--color-text-primary': '#1c0a0a',
      '--color-text-secondary': 'rgba(28,10,10,0.6)',
      '--color-text-muted': 'rgba(28,10,10,0.38)',
      '--color-input-bg': '#ffffff',
      '--color-input-border': 'rgba(220,38,38,0.2)',
      '--color-input-focus': 'rgba(220,38,38,0.18)',
      '--color-divider': 'rgba(28,10,10,0.08)',
      '--color-section-label': '#dc2626',
      '--color-btn-primary-bg': '#dc2626',
      '--color-btn-primary-text': '#ffffff',
      '--color-btn-primary-hover': '#b91c1c',
      '--color-btn-outline-border': 'rgba(220,38,38,0.35)',
      '--color-btn-outline-text': '#dc2626',
      '--color-tab-active-bg': '#dc2626',
      '--color-tab-active-text': '#ffffff',
      '--color-tab-inactive-text': 'rgba(28,10,10,0.45)',
      '--color-badge-bg': 'rgba(220,38,38,0.1)',
      '--color-badge-text': '#b91c1c',
      '--font-heading': "'DM Serif Display', 'Georgia', serif",
      '--font-body': "'DM Sans', 'Inter', sans-serif",
      '--radius-card': '0.75rem',
      '--radius-btn': '0.5rem',
      '--radius-input': '0.5rem',
    },
  },
  {
    name: 'sage',
    label: 'Sage',
    description: 'Organic calm and clarity — warm cream, sage green, terracotta warmth.',
    preview: { bg: '#faf7f2', sidebar: '#2e4a38', accent: '#5a7a5e', card: '#ffffff' },
    vars: {
      '--color-bg': '#faf7f2',
      '--color-sidebar': '#2e4a38',
      '--color-sidebar-text': 'rgba(240,235,225,0.88)',
      '--color-sidebar-active-bg': 'rgba(255,255,255,0.16)',
      '--color-sidebar-active-text': '#d4edda',
      '--color-sidebar-hover-bg': 'rgba(255,255,255,0.09)',
      '--color-sidebar-border': 'rgba(255,255,255,0.1)',
      '--color-sidebar-logo-text': '#f0ebe1',
      '--color-primary': '#5a7a5e',
      '--color-primary-light': 'rgba(90,122,94,0.1)',
      '--color-accent': '#c0714a',
      '--color-accent-dark': '#a85c38',
      '--color-accent-light': 'rgba(192,113,74,0.1)',
      '--color-card-bg': '#ffffff',
      '--color-card-border': 'rgba(90,122,94,0.12)',
      '--color-card-shadow': '0 2px 16px rgba(46,74,56,0.07)',
      '--color-text-primary': '#1a2e1e',
      '--color-text-secondary': 'rgba(26,46,30,0.62)',
      '--color-text-muted': 'rgba(26,46,30,0.4)',
      '--color-input-bg': '#ffffff',
      '--color-input-border': 'rgba(90,122,94,0.2)',
      '--color-input-focus': 'rgba(90,122,94,0.18)',
      '--color-divider': 'rgba(90,122,94,0.1)',
      '--color-section-label': '#5a7a5e',
      '--color-btn-primary-bg': '#5a7a5e',
      '--color-btn-primary-text': '#ffffff',
      '--color-btn-primary-hover': '#4a6a4e',
      '--color-btn-outline-border': 'rgba(90,122,94,0.28)',
      '--color-btn-outline-text': '#3d5a47',
      '--color-tab-active-bg': '#5a7a5e',
      '--color-tab-active-text': '#ffffff',
      '--color-tab-inactive-text': 'rgba(26,46,30,0.5)',
      '--color-badge-bg': 'rgba(90,122,94,0.12)',
      '--color-badge-text': '#3d5a47',
      '--font-heading': "'Cormorant Garamond', 'Playfair Display', serif",
      '--font-body': "'Jost', 'Lato', sans-serif",
      '--radius-card': '1rem',
      '--radius-btn': '2rem',
      '--radius-input': '0.875rem',
    },
  },
  {
    name: 'midnight',
    label: 'Midnight',
    description: 'Premium light elegance — soft lavender canvas, electric violet, cyan highlights.',
    preview: { bg: '#f3f1ff', sidebar: '#1e1b4b', accent: '#7c3aed', card: '#ffffff' },
    vars: {
      '--color-bg': '#f3f1ff',
      '--color-sidebar': '#1e1b4b',
      '--color-sidebar-text': 'rgba(220,215,255,0.88)',
      '--color-sidebar-active-bg': 'rgba(124,58,237,0.3)',
      '--color-sidebar-active-text': '#c4b5fd',
      '--color-sidebar-hover-bg': 'rgba(255,255,255,0.07)',
      '--color-sidebar-border': 'rgba(124,58,237,0.18)',
      '--color-sidebar-logo-text': '#ede9fe',
      '--color-primary': '#7c3aed',
      '--color-primary-light': 'rgba(124,58,237,0.1)',
      '--color-accent': '#0891b2',
      '--color-accent-dark': '#0e7490',
      '--color-accent-light': 'rgba(8,145,178,0.1)',
      '--color-card-bg': '#ffffff',
      '--color-card-border': 'rgba(124,58,237,0.12)',
      '--color-card-shadow': '0 2px 16px rgba(30,27,75,0.08)',
      '--color-text-primary': '#1e1b4b',
      '--color-text-secondary': 'rgba(30,27,75,0.6)',
      '--color-text-muted': 'rgba(30,27,75,0.38)',
      '--color-input-bg': '#ffffff',
      '--color-input-border': 'rgba(124,58,237,0.2)',
      '--color-input-focus': 'rgba(124,58,237,0.18)',
      '--color-divider': 'rgba(30,27,75,0.08)',
      '--color-section-label': '#7c3aed',
      '--color-btn-primary-bg': '#7c3aed',
      '--color-btn-primary-text': '#ffffff',
      '--color-btn-primary-hover': '#6d28d9',
      '--color-btn-outline-border': 'rgba(124,58,237,0.35)',
      '--color-btn-outline-text': '#7c3aed',
      '--color-tab-active-bg': '#7c3aed',
      '--color-tab-active-text': '#ffffff',
      '--color-tab-inactive-text': 'rgba(30,27,75,0.45)',
      '--color-badge-bg': 'rgba(124,58,237,0.1)',
      '--color-badge-text': '#6d28d9',
      '--font-heading': "'Space Grotesk', 'Plus Jakarta Sans', sans-serif",
      '--font-body': "'Space Grotesk', 'Inter', sans-serif",
      '--radius-card': '0.875rem',
      '--radius-btn': '0.5rem',
      '--radius-input': '0.625rem',
    },
  },
  {
    name: 'planix',
    label: 'Planix',
    description: 'Clean and professional — sky blue primary, crisp white cards, modern SaaS aesthetic.',
    preview: { bg: '#eef5fb', sidebar: '#1873cd', accent: '#1873cd', card: '#ffffff' },
    vars: {
      '--color-bg': '#eef5fb',
      '--color-sidebar': '#1873cd',
      '--color-sidebar-text': 'rgba(255,255,255,0.88)',
      '--color-sidebar-active-bg': 'rgba(255,255,255,0.18)',
      '--color-sidebar-active-text': '#ffffff',
      '--color-sidebar-hover-bg': 'rgba(255,255,255,0.1)',
      '--color-sidebar-border': 'rgba(255,255,255,0.14)',
      '--color-sidebar-logo-text': '#ffffff',
      '--color-primary': '#1873cd',
      '--color-primary-light': 'rgba(24,115,205,0.1)',
      '--color-accent': '#3d9be9',
      '--color-accent-dark': '#125fa8',
      '--color-accent-light': 'rgba(61,155,233,0.12)',
      '--color-card-bg': '#ffffff',
      '--color-card-border': 'rgba(24,115,205,0.1)',
      '--color-card-shadow': '0 4px 20px rgba(24,115,205,0.08)',
      '--color-text-primary': '#0d2a45',
      '--color-text-secondary': 'rgba(13,42,69,0.6)',
      '--color-text-muted': 'rgba(13,42,69,0.4)',
      '--color-input-bg': '#ffffff',
      '--color-input-border': 'rgba(24,115,205,0.2)',
      '--color-input-focus': 'rgba(24,115,205,0.2)',
      '--color-divider': 'rgba(24,115,205,0.1)',
      '--color-section-label': '#1873cd',
      '--color-btn-primary-bg': '#1873cd',
      '--color-btn-primary-text': '#ffffff',
      '--color-btn-primary-hover': '#125fa8',
      '--color-btn-outline-border': 'rgba(24,115,205,0.3)',
      '--color-btn-outline-text': '#1873cd',
      '--color-tab-active-bg': '#1873cd',
      '--color-tab-active-text': '#ffffff',
      '--color-tab-inactive-text': 'rgba(13,42,69,0.5)',
      '--color-badge-bg': 'rgba(24,115,205,0.1)',
      '--color-badge-text': '#1873cd',
      '--font-heading': "'Inter', 'Plus Jakarta Sans', sans-serif",
      '--font-body': "'Inter', 'Plus Jakarta Sans', sans-serif",
      '--radius-card': '1rem',
      '--radius-btn': '0.75rem',
      '--radius-input': '0.75rem',
    },
  },
];
